import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import Newsletter from "@/components/site/Newsletter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
    try {
        return new Date(d).toLocaleDateString("en-AU", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    } catch {
        return "";
    }
}

export default function InsightDetail() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [status, setStatus] = useState("loading");

    useEffect(() => {
        let alive = true;
        setStatus("loading");
        axios
            .get(`${API}/posts/${slug}`)
            .then((r) => {
                if (!alive) return;
                setPost(r.data);
                setStatus("ok");
            })
            .catch(() => alive && setStatus("missing"));
        return () => {
            alive = false;
        };
    }, [slug]);

    return (
        <main data-testid="insight-detail-page" className="bg-bone text-ink">
            <Nav />

            <article className="pt-32 md:pt-40 lg:pt-44 pb-24 md:pb-32">
                <div className="max-w-[1100px] mx-auto px-6 md:px-12">
                    <Link
                        to="/insights"
                        data-testid="insight-back"
                        className="eyebrow text-ink2 hover:text-ochre transition-colors"
                    >
                        ← All insights
                    </Link>

                    {status === "loading" && (
                        <div className="mt-10 animate-pulse space-y-4">
                            <div className="h-3 w-24 bg-rule" />
                            <div className="h-10 w-3/4 bg-rule" />
                            <div className="h-4 w-full bg-rule" />
                        </div>
                    )}

                    {status === "missing" && (
                        <div
                            data-testid="insight-missing"
                            className="mt-16 border-t border-rule pt-10"
                        >
                            <h1 className="font-serif text-4xl md:text-5xl text-ink">
                                Not found.
                            </h1>
                            <p className="mt-6 text-ink2 max-w-xl">
                                That insight isn't here. Browse all insights
                                instead.
                            </p>
                            <Link
                                to="/insights"
                                className="mt-10 inline-flex px-6 py-3 bg-ink text-bone text-[11px] tracking-[0.22em] uppercase hover:bg-eucalyptus transition-colors"
                            >
                                Back to insights
                            </Link>
                        </div>
                    )}

                    {status === "ok" && post && (
                        <>
                            <header className="mt-10 mb-12">
                                <div className="flex items-center gap-4 mb-6">
                                    <span className="number-mark text-ochre">
                                        {post.category || "Note"}
                                    </span>
                                    <span className="block w-6 h-px bg-ink2" />
                                    <span className="text-[12px] tracking-wide text-ink2">
                                        {formatDate(post.published_at)}
                                    </span>
                                </div>
                                <h1
                                    data-testid="insight-title"
                                    className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] text-ink max-w-4xl"
                                >
                                    {post.title}
                                </h1>
                                {post.author && (
                                    <p className="mt-6 italic text-ink2">
                                        By {post.author}
                                    </p>
                                )}
                            </header>

                            {post.image && (
                                <div className="overflow-hidden border border-rule mb-12">
                                    <img
                                        src={post.image}
                                        alt=""
                                        className="w-full aspect-[16/9] object-cover"
                                    />
                                </div>
                            )}

                            <div
                                data-testid="insight-body"
                                className="text-ink2 text-lg md:text-[19px] leading-[1.8] space-y-6 max-w-3xl"
                            >
                                {post.body
                                    .split(/\n\n+/)
                                    .map((para, i) => (
                                        <p key={i}>{para}</p>
                                    ))}
                            </div>
                        </>
                    )}
                </div>
            </article>

            <Newsletter />
            <Footer />
        </main>
    );
}
